import cn from 'classnames';
import type { ReactElement } from 'react';
import React, {
    Children,
    cloneElement,
    useState,
    useCallback,
    useRef,
    useMemo,
    useEffect,
    isValidElement,
} from 'react';

import type { TToggleHandler } from '../context';
import { AccordionContext } from '../context';
import type { TAccordion, TAccordionItem } from '../models';
import type stylesMobile from '../styles/mobile.css';

export const Accordion = ({
    children,
    iconPosition = 'left',
    inScrollableContainer = false,
    dividerEnabled = true,
    keepMounted = false,
    notPadded = false,
    onChange = () => {},
    openedID = '',
    styles,
    scrollableContainerId,
    subscroll = false,
    isMultipleOpen = false,
    subscrollOffset,
    theme = 'grey',
    className,
}: TAccordion) => {
    const [openedValue, setOpenedValue] = useState<string[]>(openedID ? [openedID] : []);
    const [accordionYCoordinate, setAccordionYCoordinate] = useState<number>(0);
    const [scrollableContainer, setScrollableContainer] = useState<HTMLElement | Window | null>();
    const accordionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setScrollableContainer(
            scrollableContainerId ? document.getElementById(scrollableContainerId) : window
        );
    }, [scrollableContainerId]);

    useEffect(
        () =>
            setAccordionYCoordinate(
                (scrollableContainerId
                    ? accordionRef.current?.offsetTop
                    : accordionRef.current?.getBoundingClientRect().y) || 0
            ),
        [accordionRef, scrollableContainerId]
    );

    const targetPoint = useMemo(
        () => accordionYCoordinate - (subscrollOffset || 0),
        [accordionYCoordinate, subscrollOffset]
    );
    /** Добавлен таймаут, т.к. в Safari подскрол срабатывает, прежде чем открывается вкладка */
    const subscrollHandler = useCallback(
        () =>
            setTimeout(() => {
                scrollableContainer?.scrollTo({
                    top: targetPoint,
                    behavior: 'smooth',
                });
            }),
        [scrollableContainer, targetPoint]
    );

    const subscrollHandlerWithoutOffset = useCallback(() => {
        setTimeout(() => {
            accordionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });
    }, []);

    /**
     * Обновляем в состоянии ключ раскрытого блока.
     * Если нажимаем на уже раскрытый блок, то он скрывается.
     */
    const handleToggle: TToggleHandler = ({ event, id }) => {
        /* istanbul ignore next */
        if (typeof id !== 'string') return;

        if (isMultipleOpen) {
            if (openedValue.includes(id)) {
                setOpenedValue(prevState => prevState?.filter(el => el !== id));
            } else {
                setOpenedValue(prevState => [...prevState, id]);
            }

            onChange({ event, id: openedValue });
        } else {
            const newOpenedValue = openedValue.includes(id) ? [] : [id];

            setOpenedValue(newOpenedValue);
            onChange({ event, id: newOpenedValue });
        }

        if (subscroll) {
            if (inScrollableContainer && subscrollOffset === undefined && !scrollableContainerId) {
                subscrollHandlerWithoutOffset();
            } else {
                subscrollHandler();
            }
        }
    };

    /** Новая светлая и темная тема */
    const newDesign = theme === 'light_y24' || theme === 'dark_y24';
    const classes =
        cn(
            !notPadded && !newDesign && (styles as typeof stylesMobile).accordionWrapper,
            { [styles[`${theme}Theme` as keyof typeof styles]]: !newDesign },
            className
            // Что бы не плодить class=""
        ) || undefined;

    let prevChild: ReactElement;

    const lastChildIndex = useMemo(() => {
        let findLastIndex = 0;
        Children.forEach(children, (child, index) => {
            if (isValidElement<TAccordionItem>(child)) {
                findLastIndex = index;
            }
        });

        return findLastIndex;
    }, [children]);

    return (
        <AccordionContext.Provider
            value={{
                handleToggle,
                openedValue,
                keepMounted,
                theme,
                iconPosition: newDesign ? 'right' : iconPosition,
                dividerEnabled: newDesign ? false : dividerEnabled,
                newDesign,
            }}
        >
            <div ref={accordionRef} className={classes}>
                {Children.map(children, (child, currentIndex) => {
                    if (isValidElement<TAccordionItem>(child)) {
                        let prevChildBackgroundColor: string | undefined = undefined;

                        if (prevChild) {
                            prevChildBackgroundColor = prevChild?.props?.backgroundColor;
                        }

                        const result = cloneElement(child, {
                            panelKey: currentIndex,
                            prevChildBackgroundColor,
                            isLastChild: lastChildIndex === currentIndex,
                        });

                        prevChild = child;

                        return result;
                    }
                })}
            </div>
        </AccordionContext.Provider>
    );
};
